import { tierLabel } from '../../labels.js';
import { dayMonthTime } from '../../day.js';
import names from './catalogue.json';
import type { FeatureKey } from '../../parts/access.js';
import type { AccessPageData, Addition, Change, DomainTier, Group, Person } from './types.js';

// The chosen drawings' short headings; the shared tier descriptions remain the explanatory title.
export const subjects: Record<DomainTier, string> = {
  C1: 'Client and prospect conversations', C2: 'Pricing and deal terms', L1: 'Contracts', F1: 'Client invoicing',
  L2: 'Corporate and board', L3: 'Disputes and investigations', H1: 'People operations', H2: 'Matters about a named employee',
  F2: 'Pay and compensation', F3: 'Investors and fundraising', M1: 'Brand and events', O1: 'Operations', I1: 'Industry',
};
export const sensitive: DomainTier[] = ['C2', 'L1', 'F1', 'L2', 'L3', 'H1', 'H2', 'F2', 'F3'];
export const allSubjects = Object.keys(subjects) as DomainTier[];
export const subjectDescription = (tier: DomainTier) => tierLabel(tier)?.gist;
export const ringNote = 'Pay and investors are locked to Exec by your decision in September. Changing that takes a decision from you, not a tick.';
export const ring = (tier: string) => tier === 'F2' || tier === 'F3';
export const when = (value: string | null) => value ? dayMonthTime(new Date(value)) : 'Not yet';
export const firstName = (person: Person) => person.displayName.split(' ')[0]!;
export const joinNames = (values: string[]) => values.length < 2 ? values[0] ?? '' : `${values.slice(0, -1).join(', ')}${values.length > 2 ? ',' : ''} and ${values.at(-1)}`;
export const emptyGroupsLine = (groups: Group[]) => {
  const empty = groups.filter(g => g.members === 0).map(g => g.name);
  return empty.length ? `${joinNames(empty)} ${empty.length === 1 ? 'has nobody in it' : 'have nobody in them'} yet.` : '';
};
export const featureName = (key: string, data: AccessPageData) => data.catalogue.find(f => f.key === key)?.name ?? names[key as FeatureKey] ?? 'This item';
export const groupOf = (person: Person, data: AccessPageData) => data.groups.find(g => g.id === person.groupId);
export const additionName = (a: Addition, data: AccessPageData) => a.kind === 'feature' ? featureName(a.key, data) : subjects[a.key as DomainTier] ?? 'This subject';
export const additionSource = (a: Addition) => a.carriedOver ? 'Carried over from the old settings, no reason yet'
  : [a.reason || 'Reason not recorded', a.givenBy.displayName, a.givenAt ? when(a.givenAt) : ''].filter(Boolean).join('; ');
export function beyondGroup(person: Person, data: AccessPageData): Addition[] {
  const group = groupOf(person, data);
  return person.additions.filter(a => a.kind === 'feature' ? !group?.features.some(f => f.key === a.key && f.enabled)
    : a.access !== 'none' && !group?.reading.some(r => r.domainTier === a.key && r.access !== 'none'));
}
export function readingOf(person: Person, tier: DomainTier, group?: Group): boolean {
  const exception = person.additions.find(a => a.kind === 'exception' && a.key === tier);
  if (exception) return exception.access !== 'none';
  return person.additions.some(a => a.kind === 'grant' && a.key === tier) || !!group?.reading.some(r => r.domainTier === tier && r.access !== 'none');
}
export function additionChange(person: Person, addition: Addition, remove: boolean): Change {
  if (addition.kind === 'feature') return { kind: 'person_feature', principalId: person.principalId, featureKey: addition.key as FeatureKey, enabled: !remove };
  if (addition.kind === 'grant') return { kind: 'person_reading', principalId: person.principalId, domainTier: addition.key as DomainTier, via: 'grant', enabled: !remove };
  return { kind: 'person_reading', principalId: person.principalId, domainTier: addition.key as DomainTier, via: 'exception', access: remove ? null : addition.access as 'read' | 'write' | 'none' };
}
export function personReadingChange(person: Person, tier: DomainTier, enabled: boolean, group?: Group): Change {
  const existing = person.additions.find(a => a.kind === 'exception' && a.key === tier);
  if (existing || group?.reading.some(r => r.domainTier === tier && r.access !== 'none')) {
    return { kind: 'person_reading', principalId: person.principalId, domainTier: tier, via: 'exception', access: enabled ? 'read' : 'none' };
  }
  return { kind: 'person_reading', principalId: person.principalId, domainTier: tier, via: 'grant', enabled };
}
export const enabledChange = (change: Change) => 'enabled' in change ? change.enabled : 'access' in change ? !!change.access && change.access !== 'none' : true;
export function describeChange(change: Change, data: AccessPageData, saved = false, fallback = ''): string {
  const person = 'principalId' in change ? data.people.find(p => p.principalId === change.principalId) : undefined;
  const group = 'groupId' in change ? data.groups.find(g => g.id === change.groupId) : undefined;
  const who = group?.name ?? (person ? firstName(person) : 'Your trial');
  const enabled = enabledChange(change);
  let words: string;
  switch (change.kind) {
    case 'group_reading':
    case 'person_reading': {
      const reads = change.kind === 'person_reading' && change.via === 'exception' && change.access === null
        ? !!(person && groupOf(person, data)?.reading.some(r => r.domainTier === change.domainTier && r.access !== 'none')) : enabled;
      const removesWrite = change.kind === 'group_reading' && !enabled && group?.reading.some(r => r.domainTier === change.domainTier && r.access === 'write');
      words = `${who} ${saved ? reads ? 'reads' : 'no longer reads' : enabled ? 'starts reading' : removesWrite ? 'stops reading and writing' : 'stops reading'} ${subjects[change.domainTier]}`;
      break;
    }
    case 'group_feature':
    case 'person_feature':
    case 'keeper_trial':
      words = saved ? `${who} ${enabled ? 'gets' : 'no longer gets'} ${featureName(change.featureKey, data)}`
        : person ? `${enabled ? 'Give' : 'Take back from'} ${firstName(person)} ${featureName(change.featureKey, data)}?`
          : `${who} ${enabled ? 'starts getting' : 'stops getting'} ${featureName(change.featureKey, data)}`;
      break;
    case 'group_processing':
      words = `${who} has ${change.historyMonths} months read in full and quick triage ${change.quickTriage ? 'on' : 'off'}`;
      break;
    case 'person_history_whole':
      words = `Whole history is ${enabled ? '' : 'no longer '}read for ${who}`;
      break;
    default: return fallback;
  }
  return saved ? `${words}.` : words;
}
const internalWords = /\b(?:DX|R|D|U)\d+\b|\b(?:L[123]|F[123]|H[12]|C[12]|M1|O1|I1)\b|decision in force/i;
export function errorWords(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message.trim() : '';
  return message && !internalWords.test(message) && !/\b[45]\d{2}\b|[{}<>]|https?:|\b(?:Error|Exception):/.test(message) ? message : fallback;
}
/** Server authority, without a page role test. Never draw internal decision or tier references. */
export function lockWords(why: string | null | undefined, writable = true, keeperName?: string | null) {
  const keeper = `Only ${keeperName || 'the keeper'} can change Exec.`;
  if (!writable) return keeperName || !why || internalWords.test(why) ? keeper : why;
  if (!why) return '';
  if (/DX11|Only the keeper can change Exec\./.test(why)) return keeper;
  return internalWords.test(why) ? 'Exec only, locked' : why;
}
